import React from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import { Button, Dialog, Input, Uploader } from '@app/components';
import { ActivityLogInfo } from '@app/types';
import {
  ModalContainer,
  ChecklistInfoBox,
  Footer,
  ChecklistButtonContainer,
  CancelButton,
  SavingIndicator,
  InputCard,
  ChecklistText,
} from '@app/modules/transaction-details/transactions-details-components';
import TransactionlogoUrl from '@app/assets/images/Transactionblack.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { primaryColor } from '@app/styles';

import { useUpdateChecklistMutation } from '@app/graphql';

type Props = {
  onModalClose?: any;
  userFirebaseId: string;
  instructions?: string;
  loadChecklistData: (checklistData: any, pendingTasks: string) => void;
  getActivityLogData: (e: ActivityLogInfo[]) => void;
  TaxProofData: any;
  status?: string;
};

let activityLog: ActivityLogInfo[] = [];

export default function TranscationDetails(props: Props) {
  const {
    onModalClose,
    userFirebaseId,
    instructions,
    loadChecklistData,
    TaxProofData,
    status,
    getActivityLogData,
  } = props;
  const { register } = useForm();
  const { transactionId }: any = useParams();
  const [updateCheckList] = useUpdateChecklistMutation();
  const [saving, updatesaving] = React.useState<boolean>(false);
  const [fileName, setFileName] = React.useState<string>('');
  const [taxProofData, setTaxProofData] = React.useState({
    receiptNo: '',
    userType: '',
    date: moment().format('YYYY-MM-DD'),
    image: TransactionlogoUrl,
  });

  React.useEffect(() => {
    if (TaxProofData) {
      activityLog = [];
      const {
        taxPaymentReceiptNumber = '',
        dateofTaxPayment = moment().format('YYYY-MM-DD'),
        image = '',
        fileName = '',
        userType = ''
      } = TaxProofData;
      setTaxProofData({
        receiptNo: taxPaymentReceiptNumber,
        userType: userType,
        date: dateofTaxPayment
          ? moment(dateofTaxPayment).format('YYYY-MM-DD')
          : moment().format('YYYY-MM-DD'),
        image: image ? image : TransactionlogoUrl,
      });
      setFileName(fileName);
    }
  }, [TaxProofData]);

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.name === 'receiptNumber') {
      setTaxProofData({
        ...taxProofData,
        receiptNo: event.target.value,
      });
    }
    if (event.target.name === 'paymentDate') {
      setTaxProofData({
        ...taxProofData,
        date: event.target.value,
      });
    }
  };
  const onImageChange = (event: any) => {
    if (event.target.files && event.target.files[0]) {
      setFileName(event.target.files[0].name);
      const reader = new FileReader();
      reader.onload = (e: any) => {
        setTaxProofData({
          ...taxProofData,
          image: e.target.result,
        });
      };
      reader.readAsDataURL(event.target.files[0]);
    }
  };

  const getActivityLog = () => {
    if (status !== 'COMPLETED') {
      activityLog.push({
        transactionId: transactionId,
        firebaseId: userFirebaseId,
        item: `Response`,
        description: 'for Proof of Tax Payment task has been submitted by ',
      });
      return activityLog;
    } else {
      if (TaxProofData.taxPaymentReceiptNumber !== taxProofData.receiptNo) {
        activityLog.push({
          transactionId: transactionId,
          firebaseId: userFirebaseId,
          item: `Response`,
          description: 'for Proof of Tax Payment task has been updated by ',
        });
      } else if (
        moment(TaxProofData.dateofTaxPayment).format('YYYY-MM-DD') !==
        taxProofData.date
      ) {
        activityLog.push({
          transactionId: transactionId,
          firebaseId: userFirebaseId,
          item: `Response`,
          description: 'for Proof of Tax Payment task has been updated by ',
        });
      }
      return activityLog;
    }
  };

  const submitChecklistData = () => {
    updatesaving(true);
    const checklistVariables = {
      transactionId: transactionId,
      status: 'COMPLETED',
      userType: taxProofData && taxProofData.userType ? taxProofData.userType : '',
      assignmentChecklistKey: 'proofofTaxPayment',
      completedAt: moment().format('YYYY-MM-DD'),
      taxPaymentReceiptNumber: taxProofData.receiptNo,
      dateofTaxPayment: taxProofData.date,
      firebaseId: userFirebaseId,
      activityLog: getActivityLog(),
      proofofTaxPaymentDocuments: [
        {
          name: fileName,
          src:
            taxProofData.image === TransactionlogoUrl ? '' : taxProofData.image,
          uploadedDate: moment().format('YYYY-MM-DD'),
        },
      ],
    };
    updateCheckList({
      variables: checklistVariables,
    }).then((res: any) => {
      if (res.data.updateTransactionChecklist.success) {
        const {
          updateTransactionChecklist: { transaction },
        } = res.data;
        const { assignmentChecklist, activityLog, pendingTasks } = transaction;
        loadChecklistData(assignmentChecklist, pendingTasks);
        getActivityLogData(activityLog);
        updatesaving(false);
        setTimeout(onModalClose, 500);
      }
    });
  };
  const deleteImage = () => {
    setTaxProofData({ ...taxProofData, image: TransactionlogoUrl });
    setFileName('');
  };

  return (
    <Dialog title="Proof of Tax payment" onClose={onModalClose}>
      <ModalContainer>
        <ChecklistText>{instructions}</ChecklistText>
        <ChecklistInfoBox>
          <InputCard>
            <Input
              label="Receipt Number"
              placeholder="Enter Receipt Number "
              value={taxProofData.receiptNo}
              name="receiptNumber"
              onChange={onChange}
              register={register({})}
            />
          </InputCard>
          <InputCard>
            <Input
              type="date"
              label=" Date of Payment"
              name="paymentDate"
              value={taxProofData.date}
              onChange={onChange}
              register={register({})}
            />
          </InputCard>
          <InputCard>
            <Uploader
              label=" Upload the Receipt"
              profileImage={taxProofData.image}
              fileName={fileName}
              chooseText="Browse File"
              onChange={e => onImageChange(e)}
              borderRadius="8px"
              onImageDelete={deleteImage}
            />
          </InputCard>
        </ChecklistInfoBox>
      </ModalContainer>
      <Footer>
        {saving && (
          <SavingIndicator>
            <FontAwesomeIcon
              color={primaryColor}
              icon={faCircleNotch}
              size="lg"
              spin
            />
          </SavingIndicator>
        )}
        <CancelButton>
          <Button variant="text" size="medium" onClick={onModalClose}>
            Cancel
          </Button>
        </CancelButton>
        <ChecklistButtonContainer
          disabled={saving}
          onClick={submitChecklistData}>
          <Button variant="primary" size="medium" width="100%" type="submit">
            Submit Task
          </Button>
        </ChecklistButtonContainer>
      </Footer>
    </Dialog>
  );
}
